// --------------------------------------------------------------
// Created On: 2024-05-01
// Author: KC Willard
//
// Last Modified: 2025-11-06
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState, useMemo, useEffect } from "react";
import Error500Page from "../Error500Page/Error500Page";
import Spinner from "../../components/Spinner/Spinner";
import useApi from "../../hooks/useApi";
import CustomerForm from "./CustomerForm/CustomerForm";
import Accordion from "./Accordion/Accordion";
import apiRequest from "../../utilities/api/apiRequest";
import deepCopy from "../../utilities/deepCopy";
import findExistingCartItem from "../../utilities/findExistingCartItem";
import SaveCustomerAddressModal from "./SaveCustomerAddressModal/SaveCustomerAddressModal";
import ShippingForm from "./ShippingForm/ShippingForm";
import ShoppingCart from "../../components/ShoppingCart/ShoppingCart";
import ConfirmOrderForm from "./ConfirmOrderForm/ConfirmOrderForm";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";
import getApiError from "../../utilities/api/getApiError";
import Toast from "../../components/Toast/Toast";
import { useNavigate, useParams } from "react-router-dom";
import { API, COUNTRY_LIST, MS_PER_SECOND } from "../../constants/miscellaneous";
import delayMilliseconds from "../../utilities/delayMilliseconds";
import useReduxSelector from "src/hooks/useReduxSelector";
import ProcessingOrderModal from "./ProcessingOrderModal/ProcessingOrderModal";
import { postcodeValidator, postcodeValidatorExistsForCountry } from "postcode-validator";
import isoToDate from "../../utilities/time/isoToDate";
import { CARRIER_NAMES } from "../../constants/miscellaneous";

// Page that contains an accordion with four forms that the user is required to fill out
// before being able to submit the order to Epicor.
export default function SubmitOrderPage(props: Props): Component {
  //Constants
  const SALES_ORDER_RETRY_LIMIT = 90;
  const SALES_ORDER_DELAY_SECONDS = 5;
  const navigate = useNavigate();
  const { quoteId } = useParams();
  const initialUser = {
    name: "",
    companyName: "",
    email: "",
    phoneNumber: "",
    savedCustomers: [],
    companyCode: "",
    companyDiscount: 0,
    distributorAccountCode: null,
  };
  // Page states
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [failedToLoad, setFailedToLoad] = useState<boolean>(false);
  const [showProcessingModal, setShowProcessingModal] = useState<boolean>(false);
  const [isQuote, setIsQuote] = useState<boolean>(false);
  // Accordion states
  const [customerFormIsActive, setCustomerFormIsActive] = useState<boolean>(true);
  const [shippingFormIsActive, setShippingFormIsActive] = useState<boolean>(false);
  const [shoppingCartIsActive, setShoppingCartIsActive] = useState<boolean>(false);
  const [confirmOrderFormIsActive, setConfirmOrderFormIsActive] = useState<boolean>(false);
  const [customerFormIsValid, setCustomerFormIsValid] = useState<boolean | null>(null);
  const [shippingFormIsValid, setShippingFormIsValid] = useState<boolean | null>(null);
  const [shoppingCartIsValid, setShoppingCartIsValid] = useState<boolean | null>(null);
  const [confirmOrderFormIsValid, setConfirmOrderFormIsValid] = useState<boolean | null>(null);
  // Customer Form States.
  const [customerErrorMessage, setCustomerErrorMessage] = useState<string>("");
  const [customerWarningMessage, setCustomerWarningMessage] = useState<string>("");
  const [user, setUser] = useState<User>(initialUser);
  const [poNumber, setPoNumber] = useState<string>("");
  const [quoteName, setQuoteName] = useState<string>("");
  const [shippingCountry, setShippingCountry] = useState<string>("US");
  const [shippingCompanyName, setShippingCompanyName] = useState<string>("");
  const [shippingAddressLine1, setShippingAddressLine1] = useState<string>("");
  const [shippingAddressLine2, setShippingAddressLine2] = useState<string>("");
  const [shippingContactName, setShippingContactName] = useState<string>("");
  const [shippingPhoneNumber, setShippingPhoneNumber] = useState<string>("");
  const [shippingCity, setShippingCity] = useState<string>("");
  const [shippingState, setShippingState] = useState<string>("");
  const [shippingZipCode, setShippingZipCode] = useState<string>("");
  const [selectedCustomerAddress, setSelectedCustomerAddress] = useState<SavedCustomer | null>(null);
  const [customerComment, setCustomerComment] = useState<string>("");
  const [showSavedCustomerModal, setShowSavedCustomerModal] = useState<boolean>(false);
  const [showDeleteCustomerModal, setShowDeleteCustomerModal] = useState<boolean>(false);
  // Shipping Form States.
  const [allowPartialShipments, setAllowPartialShipments] = useState<boolean>(false);
  const [shippingCarrier, setShippingCarrier] = useState<Carrier | null>(null);
  const [shipVia, setShipVia] = useState<string>("");
  const [shippingBillingAccountNumber, setShippingBillingAccountNumber] = useState<string>("");
  const [shippingSpecialInstructions, setShippingSpecialInstructions] = useState<string>("");
  // Shopping Cart States
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [quoteCartItemsShoppingCart, setQuoteCartItemsShoppingCart] = useState<QuoteCartItemShoppingCart[]>([]);
  const [previousQuoteCartItemsShoppingCart, setPreviousQuoteCartItemsShoppingCart] = useState<
    QuoteCartItemShoppingCart[]
  >([]);
  const [paintColors, setPaintColors] = useState<PaintColor[]>([]);
  // Confirm Order States
  const [consentChecked, setConsentChecked] = useState<boolean>(false);
  // useMemo Functions
  const cartHasError = useMemo(
    () => cartIsInvalid(cartItems, quoteCartItemsShoppingCart),
    [JSON.stringify(cartItems), JSON.stringify(quoteCartItemsShoppingCart)]
  );
  const orderIsValid = useMemo(
    () => isOrderValid(),
    [customerFormIsValid, shippingFormIsValid, shoppingCartIsValid, confirmOrderFormIsValid]
  );
  const cartQuantity = useReduxSelector((state) => state.cartQuantity);

  const shippingCompanyNameIsValid = shippingCompanyName !== "" && shippingCompanyName.length <= 50;
  const shippingAddressLine1IsValid = shippingAddressLine1 !== "" && shippingAddressLine1.length <= 50;
  const shippingAddressLine2IsValid = shippingAddressLine2.length <= 50;
  const shippingCityIsValid = shippingCity !== "" && shippingCity.length <= 50;
  const shippingContactNameIsValid = shippingContactName.length <= 50;
  const shippingPhoneNumberIsValid = shippingPhoneNumber.length <= 20;

  // Determine whether or not the user is viewing a quote or an order.
  useEffect(() => {
    if (props.isNewQuote || quoteId !== undefined) {
      setIsQuote(true);
    } else {
      setIsQuote(false);
    }
    setLoading(false);
  }, [quoteId]);

  // Get quote information if the user is submitting an order from a quote.
  useApi(
    () => {
      if (quoteId !== undefined) {
        setLoading(true);
        return true;
      } else {
        return false;
      }
    },
    {
      url: `${API}/sales-quote/${quoteId}`,
      method: "GET",
    },
    async (response: Response, responseBody: ResponseBodyGetSalesQuote) => {
      if (response.ok && responseBody) {
        setAllowPartialShipments(responseBody.allowPartialShipments);
        setQuoteCartItemsShoppingCart(formatQuoteCartItems(responseBody.cart));
        setPreviousQuoteCartItemsShoppingCart(formatQuoteCartItems(responseBody.cart));
        setCustomerComment(responseBody.comment === null ? "" : responseBody.comment);
        setPoNumber(responseBody.poNumber);
        setQuoteName(responseBody.quoteName);
        setShippingAddressLine1(responseBody.shippingAddressLine1);
        setShippingAddressLine2(responseBody.shippingAddressLine2 === null ? "" : responseBody.shippingAddressLine2);
        setShippingCity(responseBody.shippingCity);
        setShippingCompanyName(responseBody.shippingCompanyName);
        setShippingContactName(responseBody.shippingContactName);
        setShippingCountry(responseBody.shippingCountry);
        setShippingPhoneNumber(responseBody.shippingPhoneNumber);
        setShippingState(responseBody.shippingState);
        setShippingZipCode(responseBody.shippingZipCode.toString());
        setShippingBillingAccountNumber(
          responseBody.shippingBillingAccountNumber === null ? "" : responseBody.shippingBillingAccountNumber
        );
        setShippingSpecialInstructions(
          responseBody.specialInstructions === null ? "" : responseBody.specialInstructions
        );
        const carrier: Carrier | undefined = CARRIER_NAMES.find((carrier) => {
          if (carrier.name === responseBody.shipVia) {
            return carrier;
          } else if (carrier.types.length > 0) {
            return carrier.types.find((type) => type.code === responseBody.shipVia);
          }
        });
        if (carrier === undefined) {
          setShippingCarrier(null);
          setShipVia("");
        } else if (carrier.name === "Contact Customer") {
          setShippingCarrier(carrier);
          setShipVia(carrier.types[0].code);
        } else {
          setShippingCarrier(carrier);
          const shipViaType = carrier.types.find((type) => type.code === responseBody.shipVia);
          if (shipViaType) {
            setShipVia(shipViaType.code);
          }
        }
      }
    },
    [quoteId]
  );

  // Get user information as well as shopping cart and part weight information.
  useApi(
    () => {
      setLoading(true);
      return true;
    },
    {
      url: `${API}/review-order`,
      method: "GET",
    },
    async (response: Response, responseBody: ResponseBodyMap) => {
      if (response.ok && responseBody) {
        setUser(responseBody.user);
        const cartItems: CartItem[] = [];
        quoteId === undefined &&
          responseBody.shoppingCart.items.forEach((cartItem) =>
            cartItems.push({
              cartItemId: cartItem.cartItemId,
              name: cartItem.name,
              quantity: String(cartItem.quantity),
              quantityIsValid: true,
              units: cartItem.units,
              price: cartItem.price,
              weight: cartItem.weight,
              isPaintable: cartItem.isPaintable,
              paintColorId: cartItem.paintColorId,
              partId: cartItem.partId,
              partNumber: cartItem.partNumber,
              impellerAngle: cartItem.impellerAngle
                ? parseFloat(cartItem.impellerAngle).toFixed(0).toString()
                : cartItem.impellerAngle,
              impellerAngleIsValid: true,
              impellerTrim: cartItem.impellerTrim
                ? parseFloat(cartItem.impellerTrim).toFixed(2).toString()
                : cartItem.impellerTrim,
              impellerTrimIsValid: true,
              dateAvailable: null,
            })
          );
        setCartItems(cartItems);
        setPaintColors(responseBody.shoppingCart.paintColors);
        setFailedToLoad(false);
      } else {
        setFailedToLoad(true);
      }
      setLoading(false);
    },
    [cartQuantity, quoteId]
  );

  // If zipcode information is changed, remove zipcode error/warning messages until a new check is made.
  useEffect(() => {
    setCustomerErrorMessage("");
    setCustomerWarningMessage("");
  }, [shippingZipCode]);

  // Function to ensure only one accordion can be active at a given time.
  function handleAccordionIsActive(activeAccordionIndex: number, activeState: boolean, isContinue: boolean) {
    // List of all isActive state functions. Order matches the order displayed on screen. Important!
    const accordionStateFunctions = [
      setCustomerFormIsActive,
      setShippingFormIsActive,
      setShoppingCartIsActive,
      setConfirmOrderFormIsActive,
    ];
    const accordionValidationFunctions = [
      validateCustomerForm,
      validateShippingForm,
      validateShoppingCart,
      validateConfirmOrderForm,
    ];

    const accordionStates = [
      customerFormIsActive,
      shippingFormIsActive,
      shoppingCartIsActive,
      confirmOrderFormIsActive,
    ];
    // If you're clicking away from an active tab, run the validation prior to closing.
    accordionStates.forEach((accordionState, i) => {
      if (accordionState === true) {
        accordionValidationFunctions[i]();
      }
    });

    // If the index passed in matches the index of the function, set it to true. Else set it to false.
    accordionStateFunctions.forEach((accordionStateFunction, i) => {
      if (i + 1 === activeAccordionIndex) {
        accordionStateFunction(activeState);
        // If the accordion is closing, run the validation function associated with it.
        if (activeState === false) {
          accordionValidationFunctions[i]();
        }
      } else {
        accordionStateFunction(false);
      }
    });
    // If the user has pressed the continue button, open the next accordion in the list.
    if (isContinue) {
      if (activeAccordionIndex < accordionValidationFunctions.length) {
        const nextAccordionStateFunction = accordionStateFunctions[activeAccordionIndex];
        nextAccordionStateFunction(true);
      }
    }
  }

  // Updates the selected shipping carrier, and related fields.
  function handleChangeShippingCarrier(shippingCarrier: Carrier | null): void {
    setShippingCarrier(shippingCarrier);
    if (shippingCarrier !== null && shippingCarrier.name === "Contact Customer" && shippingCarrier.types.length > 0) {
      setShipVia(shippingCarrier.types[0].code);
    } else {
      setShipVia("");
    }
    setShippingBillingAccountNumber("");
  }

  // Validate shipping information entered in the Customer Form.
  function validateShippingInformation(): boolean {
    if (
      shippingCompanyNameIsValid &&
      shippingAddressLine1IsValid &&
      shippingAddressLine2IsValid &&
      shippingContactNameIsValid &&
      shippingPhoneNumberIsValid &&
      shippingCityIsValid &&
      shippingState !== "" &&
      shippingZipCode !== ""
    ) {
      return true;
    } else {
      return false;
    }
  }

  // Customer Form functions.
  // Make API call to return the saved customer information.
  async function getSavedCustomerAddressInformation() {
    if (selectedCustomerAddress === null) {
      return;
    } else {
      setLoading(true);
      const [response, responseBody] = (await apiRequest(
        `${API}/saved-customer-address/${selectedCustomerAddress.savedCustomerAddressId}`,
        "GET",
        null
      )) as [Response, ResponseBodySavedCustomerGet];
      if (response.ok) {
        setShippingCountry(responseBody.shippingCountry);
        setShippingState(responseBody.shippingAdministrativeArea);
        setShippingCity(responseBody.shippingLocality);
        setShippingZipCode(responseBody.shippingPostalCode);
        setShippingAddressLine1(responseBody.shippingAddressLine1);
        setShippingAddressLine2(responseBody.shippingAddressLine2);
        setShippingCompanyName(responseBody.shippingCompanyName);
        setShippingContactName(responseBody.shippingContactName);
        setShippingPhoneNumber(responseBody.shippingPhoneNumber);
        setCustomerComment(responseBody.comment);
        // Need to check this to prevent the default state of the dropdown in customer form from being something other
        // than US.
        setLoading(false);
      } else {
        setErrorMessage(await getApiError(response, "Unable to get saved customer information"));
        setLoading(false);
        console.error("Internal server error. Unable to get saved customer information.");
      }
      return;
    }
  }

  // Make API call to delete the selected customer address.
  async function deleteSavedCustomerAddressInformation() {
    if (selectedCustomerAddress === null) {
      return;
    } else {
      const [response] = await apiRequest(
        `${API}/saved-customer-address/${selectedCustomerAddress.savedCustomerAddressId}`,
        "DELETE",
        null
      );
      if (response.ok) {
        const savedCustomersDeepCopy = deepCopy(user.savedCustomers);

        // Get the SavedCustomer we are updating.
        const deletedSavedCustomer = savedCustomersDeepCopy.find(
          (user) => user.savedCustomerAddressId === selectedCustomerAddress.savedCustomerAddressId
        );
        const deletedSavedCustomerIndex = savedCustomersDeepCopy.findIndex(
          (user) => user.savedCustomerAddressId === selectedCustomerAddress.savedCustomerAddressId
        );

        if (deletedSavedCustomer !== undefined && deletedSavedCustomerIndex > -1) {
          savedCustomersDeepCopy.splice(deletedSavedCustomerIndex, 1);
          user.savedCustomers = savedCustomersDeepCopy;
          setUser(user);
          setSelectedCustomerAddress(null);
          setShowDeleteCustomerModal(false);
          setShippingCountry("US");
          setShippingState("AL");
          setShippingCity("");
          setShippingZipCode("");
          setShippingAddressLine1("");
          setShippingAddressLine2("");
          setShippingCompanyName("");
          setShippingContactName("");
          setShippingPhoneNumber("");
          setCustomerComment("");
          setLoading(false);
        }
      } else {
        setErrorMessage(await getApiError(response, "Unable to delete customer address"));
        setLoading(false);
        console.error("Internal server error. Unable to delete customer address");
      }
    }
  }

  // Make API call to save the customer address.
  async function saveCustomerAddress(savedCustomerAddressName: string) {
    // Ensure that all required fields are filled in and valid.
    const requestBody = {
      entryName: savedCustomerAddressName,
      shippingCountry: shippingCountry,
      shippingAdministrativeArea: shippingState,
      shippingLocality: shippingCity,
      shippingDependentLocality: "",
      shippingPostalCode: shippingZipCode,
      shippingSortingCode: "",
      shippingAddressLine1: shippingAddressLine1,
      shippingAddressLine2: shippingAddressLine2,
      shippingCompanyName: shippingCompanyName,
      shippingContactName: shippingContactName,
      shippingPhoneNumber: shippingPhoneNumber,
      comment: customerComment,
    };
    const [response, responseBody] = (await apiRequest(`${API}/saved-customer-address`, "PUT", requestBody)) as [
      Response,
      ResponseBodySavedCustomerPut
    ];
    if (response.ok) {
      const userDeepCopy = deepCopy(user);
      // If this is a new user, add them to the list of stored users, otherwise, do nothing.
      if (
        !userDeepCopy.savedCustomers.some(
          (savedCustomer) => savedCustomer.savedCustomerAddressId === responseBody.savedCustomerAddressId
        )
      ) {
        const newCustomer = {
          savedCustomerAddressId: responseBody.savedCustomerAddressId,
          name: savedCustomerAddressName,
        };
        userDeepCopy.savedCustomers.push(newCustomer);
        setUser(userDeepCopy);
        setSelectedCustomerAddress(newCustomer);
      }
      setLoading(false);
    } else {
      setErrorMessage(await getApiError(response, "Unable to save customer address"));
      setLoading(false);
      console.error("Internal server error. Unable to save customer address");
    }
  }

  // Shopping Cart functions
  // Function that takes in a single cart item and updates its values if the values are valid.
  function updateCartItem(
    updatedItem: CartItem | QuoteCartItemShoppingCart,
    quantity: string,
    quantityIsValid: boolean,
    paintColorId: number | null,
    impellerTrim: string | null,
    impellerTrimIsValid: boolean,
    impellerAngle: string | null,
    impellerAngleIsValid: boolean
  ) {
    updatedItem.quantityIsValid = quantityIsValid;
    updatedItem.impellerTrimIsValid = impellerTrimIsValid;
    updatedItem.impellerAngleIsValid = impellerAngleIsValid;
    if (quantityIsValid) {
      updatedItem.quantity = quantity;
    }
    if (impellerTrimIsValid && impellerTrim !== null) {
      updatedItem.impellerTrim = parseFloat(impellerTrim).toFixed(2).toString();
    }
    if (impellerAngleIsValid) {
      updatedItem.impellerAngle = impellerAngle;
    }
    updatedItem.paintColorId = paintColorId;
    return updatedItem;
  }

  //Function to combine and remove duplicate items from the cart.
  function combineDuplicateItems(
    existingCartItemId: number,
    updatedItemIndex: number,
    updatedItemQuantity: string,
    cartItems: CartItem[] | QuoteCartItemShoppingCart[]
  ) {
    const existingItemIndex = cartItems.findIndex((cartItem) => cartItem.cartItemId === existingCartItemId);
    const newQuantity = Number(cartItems[existingItemIndex].quantity) + Number(updatedItemQuantity);
    cartItems[existingItemIndex].quantity = String(newQuantity);
    cartItems.splice(updatedItemIndex, 1);
    return cartItems;
  }

  // Update the cart item. If there is already a cart item with the exact configuration, update the existing cart items quantity.
  async function updateCart(
    itemId: number,
    quantity: string,
    quantityIsValid: boolean,
    paintColorId: number | null,
    impellerTrim: string | null,
    impellerTrimIsValid: boolean,
    impellerAngle: string | null,
    impellerAngleIsValid: boolean
  ) {
    let requestBody: { items: CartItem[]; replaceAll: boolean } = {
      items: [],
      replaceAll: true,
    };
    let shoppingCartItems: CartItem[] | QuoteCartItemShoppingCart[] = [];
    if (quoteId === undefined || !isQuote) {
      shoppingCartItems = cartItems;
    } else {
      shoppingCartItems = quoteCartItemsShoppingCart;
    }
    const itemsDeepCopy = deepCopy(shoppingCartItems);
    // Get the item we are updating.
    const updatedItem = itemsDeepCopy.find((cartItem) => cartItem.cartItemId === itemId);
    const updatedItemIndex = itemsDeepCopy.findIndex((cartItem) => cartItem.cartItemId === itemId);

    if (updatedItem !== undefined && updatedItemIndex > -1) {
      itemsDeepCopy[updatedItemIndex] = updateCartItem(
        updatedItem,
        quantity,
        quantityIsValid,
        paintColorId,
        impellerTrim,
        impellerTrimIsValid,
        impellerAngle,
        impellerAngleIsValid
      );

      if (quantityIsValid && impellerTrimIsValid && impellerAngleIsValid) {
        // Check to see if the cart item already exists. Update quantity if so.
        const existingCartItemId = findExistingCartItem(itemsDeepCopy[updatedItemIndex], cartItems);
        if (existingCartItemId !== null) {
          combineDuplicateItems(existingCartItemId, updatedItemIndex, quantity, itemsDeepCopy);
        }
      }
      // Set the state of the cart items.
      quoteId === undefined || !isQuote ? setCartItems(itemsDeepCopy) : setQuoteCartItemsShoppingCart(itemsDeepCopy);
      setLoading(false);
      requestBody = {
        items: itemsDeepCopy,
        replaceAll: true,
      };
    }
    const [response] = await apiRequest(`${API}/cart`, "PUT", requestBody);
    if (response.ok) {
      setLoading(false);
    } else {
      setErrorMessage(await getApiError(response, "Unable to update shopping cart."));
      setLoading(false);
    }
  }

  // Make cart put API call.
  async function cartPut(itemsDeepCopy: CartItem[] | QuoteCartItemShoppingCart[]) {
    setLoading(true);
    const requestBody = {
      items: itemsDeepCopy,
      replaceAll: true,
    };
    const [response] = await apiRequest(`${API}/cart`, "PUT", requestBody);
    if (response.ok) {
      setLoading(false);
    } else {
      setFailedToLoad(true);
      setLoading(false);
      console.error("Internal server error. Unable to update shopping cart.");
    }
  }

  // Update the dateAvailable for a given item.
  async function updateCartItemDateAvailable(itemId: number, dateAvailable: string | null) {
    if (quoteId === undefined || !isQuote) {
      setCartItems((prev) => {
        const itemsDeepCopy = deepCopy(prev);
        // Get the item we are updating.
        const updatedItem = itemsDeepCopy.find((cartItem) => cartItem.cartItemId === itemId);
        const updatedItemIndex = itemsDeepCopy.findIndex((cartItem) => cartItem.cartItemId === itemId);
        if (updatedItem !== undefined && updatedItemIndex > -1) {
          itemsDeepCopy[updatedItemIndex].dateAvailable = dateAvailable;
          return itemsDeepCopy;
        } else {
          return itemsDeepCopy;
        }
      });
    } else {
      setQuoteCartItemsShoppingCart((prev) => {
        const quoteCartItemsDeepCopy = deepCopy(prev);
        // Get the item we are updating.
        const updatedItem = quoteCartItemsDeepCopy.find((cartItem) => cartItem.cartItemId === itemId);
        const updatedItemIndex = quoteCartItemsDeepCopy.findIndex((cartItem) => cartItem.cartItemId === itemId);
        if (updatedItem !== undefined && updatedItemIndex > -1) {
          quoteCartItemsDeepCopy[updatedItemIndex].dateAvailable = dateAvailable;
          return quoteCartItemsDeepCopy;
        } else {
          return quoteCartItemsDeepCopy;
        }
      });
    }
  }

  // Delete an item from the cart
  async function deleteCartItem(itemId: number | null) {
    let shoppingCartItems: CartItem[] | QuoteCartItemShoppingCart[] = [];
    let requestBody: { items: CartItem[] | QuoteCartItemShoppingCart[]; replaceAll: boolean } = {
      items: [],
      replaceAll: true,
    };
    if (itemId === null) {
      return;
    }
    if (quoteId === undefined || !isQuote) {
      shoppingCartItems = deepCopy(cartItems);
    } else {
      shoppingCartItems = deepCopy(quoteCartItemsShoppingCart);
    }

    // Get the item we are updating.
    const deletedItem = shoppingCartItems.find((cartItem) => cartItem.cartItemId === itemId);
    const deletedItemIndex = shoppingCartItems.findIndex((cartItem) => cartItem.cartItemId === itemId);

    if (deletedItem !== undefined && deletedItemIndex > -1) {
      shoppingCartItems.splice(deletedItemIndex, 1);
      quoteId === undefined || !isQuote
        ? setCartItems(shoppingCartItems)
        : setQuoteCartItemsShoppingCart(shoppingCartItems);
      setLoading(false);
      requestBody = {
        items: shoppingCartItems,
        replaceAll: true,
      };
    }
    const [response] = await apiRequest(`${API}/cart`, "PUT", requestBody);
    if (response.ok) {
      setLoading(false);
    } else {
      setErrorMessage(await getApiError(response, "Unable to delete cart item."));
      setLoading(false);
    }
    setErrorMessage("");
  }

  // Clear the cart of all items.
  async function clearCart() {
    setCartItems([]);
    const requestBody = {
      items: [],
      replaceAll: true,
    };
    const [response] = await apiRequest(`${API}/cart`, "PUT", requestBody);
    if (response.ok) {
      setLoading(false);
    } else {
      setErrorMessage(await getApiError(response, "Unable to clear cart."));
      setLoading(false);
    }
  }

  // Function to validate all aspects of a cart item are within the acceptable bounds. Set invalid message if they are not.
  function cartIsInvalid(items: CartItem[], quoteItems: QuoteCartItemShoppingCart[]) {
    let cartItems: CartItem[] | QuoteCartItemShoppingCart[] = [];
    if (quoteId === undefined || !isQuote) {
      cartItems = items;
    } else {
      cartItems = quoteItems;
    }
    return cartItems.some((item) => {
      if (!item.quantityIsValid) {
        console.error(`Item '${item.name}' has an invalid quantity of ${item.quantity}.`);
        return true;
      } else if (!item.impellerTrimIsValid) {
        console.error(`Item '${item.name}' has an invalid impeller trim of ${item.impellerTrim}".`);
        return true;
      } else if (!item.impellerAngleIsValid) {
        console.error(`Item '${item.name}' has an invalid impeller angle of ${item.impellerAngle} degrees.`);
        return true;
      } else if (item.dateAvailable === null) {
        console.error(`Item '${item.name}' has an invalid date of '${item.dateAvailable}'.`);
        return true;
      } else {
        console.log(`Item '${item.name}' is valid.`);
        return false;
      }
    });
  }

  // Handle updating multiple paint items. If the new quantity is equal to the existing quantity, update the paint color.
  // If the quantity is different, update the existing item with the new paint color and quantity and create a new cart
  // item with the old paint color and remaining quantity.
  function updateMultiplePaintItems(itemId: number, newPaintColorId: number | null, quantity: string) {
    let shoppingCartItems: CartItem[] | QuoteCartItemShoppingCart[] = [];
    if (quoteId === undefined || !isQuote) {
      shoppingCartItems = deepCopy(cartItems);
    } else {
      shoppingCartItems = deepCopy(quoteCartItemsShoppingCart);
    }
    const updatedItemIndex = shoppingCartItems.findIndex((item) => item.cartItemId === itemId);
    const updatedItem = shoppingCartItems[updatedItemIndex];
    if (quantity === updatedItem.quantity) {
      updateCart(
        updatedItem.cartItemId,
        updatedItem.quantity,
        updatedItem.quantityIsValid,
        newPaintColorId,
        updatedItem.impellerTrim,
        updatedItem.impellerTrimIsValid,
        updatedItem.impellerAngle,
        updatedItem.impellerAngleIsValid
      );
    } else {
      const lastCartItemId = shoppingCartItems[shoppingCartItems.length - 1].cartItemId;
      const existingPaintColorId = updatedItem.paintColorId;
      const currentQuantity = updatedItem.quantity;
      // Set the new quantity to the existing item.
      shoppingCartItems[updatedItemIndex].quantity = quantity;
      shoppingCartItems[updatedItemIndex].paintColorId = newPaintColorId;
      // Create a new cart item and put it at the end of the list of cart items that has the previous quantity minus the
      // quantity selected in the paint modal.
      const newCartItem: CartItem = {
        cartItemId: lastCartItemId + 1,
        isPaintable: updatedItem.isPaintable,
        paintColorId: existingPaintColorId,
        quantity: String(parseInt(currentQuantity) - parseInt(quantity)),
        quantityIsValid: true,
        units: updatedItem.units,
        partId: updatedItem.partId,
        partNumber: updatedItem.partNumber,
        price: updatedItem.price,
        weight: updatedItem.weight,
        impellerAngle: updatedItem.impellerAngle,
        impellerAngleIsValid: true,
        impellerTrim: updatedItem.impellerTrim,
        impellerTrimIsValid: true,
        name: updatedItem.name,
        dateAvailable: updatedItem.dateAvailable,
      };
      shoppingCartItems.push(newCartItem);
      quoteId === undefined || !isQuote
        ? setCartItems(shoppingCartItems)
        : setQuoteCartItemsShoppingCart(shoppingCartItems);
      cartPut(shoppingCartItems);
    }
  }

  // Validation functions
  // Ensures that every non-optional CustomerForm field is filled in.
  function validateCustomerForm() {
    let shippingValid = false;

    // Validate the zip code is in the correct format for the selected country.
    // Zip codes are processed as valid for countries that the validator does not support.
    const isZipCodeValid = validateZipCode();
    if (validateShippingInformation() && isZipCodeValid && poNumber !== "") {
      shippingValid = true;
    }
    setCustomerFormIsValid(shippingValid);
  }

  // Attempts to validate the zip code if possible.
  // Shows appropriate error messages if zip code is invalid or cannot be checked.
  function validateZipCode(): boolean {
    let isZipCodeValid = false;
    const countryCode = COUNTRY_LIST.find((country) => country.code === shippingCountry)?.code || "";
    if (postcodeValidatorExistsForCountry(countryCode)) {
      isZipCodeValid = postcodeValidator(shippingZipCode, countryCode);
      setCustomerWarningMessage("");
    } else {
      isZipCodeValid = true;
      setCustomerWarningMessage("Zip code could not be validated for this country.");
    }

    // Display an error message if the zip code is invalid for the current country.
    if (!isZipCodeValid) {
      setCustomerErrorMessage("Zip code is invalid for the current country.");
    } else {
      setCustomerErrorMessage("");
    }
    return isZipCodeValid;
  }

  // Ensures that every non-optional ShippingForm field is filled in.
  function validateShippingForm() {
    let shippingCarrierName = "";
    if (shippingCarrier !== null) {
      shippingCarrierName = shippingCarrier.name;
    }
    const shippingCarrierRequiresBillingAccountNumber = !["Best Way", "Contact Customer"].includes(shippingCarrierName);
    if (
      shippingCarrier !== null &&
      shipVia !== "" &&
      (!shippingCarrierRequiresBillingAccountNumber || shippingBillingAccountNumber !== "")
    ) {
      setShippingFormIsValid(true);
    } else {
      setShippingFormIsValid(false);
    }
  }

  // Set state of shoppingCartIsValid based on cartIsInvalid function.
  function validateShoppingCart() {
    setShoppingCartIsValid(!cartHasError);
  }

  // Function that updates both the state of consentChecked and the orderFormIsValid states when the consent box is checked.
  function handleConsentChecked(consentChecked: boolean) {
    setConsentChecked(consentChecked);
    setConfirmOrderFormIsValid(consentChecked);
  }

  // Make sure the consent checkbox is checked before submitting order.
  function validateConfirmOrderForm() {
    setConfirmOrderFormIsValid(consentChecked);
  }

  // All forms must be visited and be correctly filled out before order is able to be submitted.
  function isOrderValid() {
    return (
      customerFormIsValid === true &&
      shippingFormIsValid === true &&
      shoppingCartIsValid === true &&
      confirmOrderFormIsValid === true
    );
  }

  // Determine the order need by/ship by dates based on whether the order is allowing partial shipments or not. If the
  // order DOES allow partial shipments, the order need by/ship by is the earliest need by/ship by in the list of parts
  // otherwise it is the latest item need by/ship by date.
  function getOrderNeedBy() {
    let maxOrderNeedBy: string | null = null;
    let minOrderNeedBy: string | null = null;
    if (quoteId === undefined || !isQuote) {
      cartItems.forEach((cartItem) => {
        if (cartItem !== null && cartItem.dateAvailable !== null) {
          if (minOrderNeedBy === null || cartItem.dateAvailable < minOrderNeedBy) {
            minOrderNeedBy = cartItem.dateAvailable;
          }
          if (maxOrderNeedBy === null || cartItem.dateAvailable > maxOrderNeedBy) {
            maxOrderNeedBy = cartItem.dateAvailable;
          }
        }
      });
    } else {
      quoteCartItemsShoppingCart.forEach((cartItem) => {
        if (cartItem !== null && cartItem.dateAvailable !== null) {
          if (minOrderNeedBy === null || cartItem.dateAvailable < minOrderNeedBy) {
            minOrderNeedBy = cartItem.dateAvailable;
          }
          if (maxOrderNeedBy === null || cartItem.dateAvailable > maxOrderNeedBy) {
            maxOrderNeedBy = cartItem.dateAvailable;
          }
        }
      });
    }
    if (allowPartialShipments) {
      return minOrderNeedBy;
    } else {
      return maxOrderNeedBy;
    }
  }

  // Parses the list of cart items and transforms into the format expected by the sales order post endpoint.
  // NOTE: This request body includes shipBy and needBy. This is to improve the speed of placing the sales order,
  // however it also allows a technically inclined user to spoof the 'shipBy' and 'needBy' dates for orders.
  // We have received clarification from Greg that this spoofed data doesn't not impact the priority of these orders
  // being shipped to the customer, but only negatively impacts the customer who is attempting to cheat the system.
  function formatCartItemsForSubmission() {
    const formattedCartItems: CartItemPost[] | QuoteCartItemPost[] = [];

    // If the we are not allowing partial shipments, then we will want to find the latest shipBy/needBy date
    // and update all parts to use it.
    let latestNeedByDate: string | null = null;
    if (!allowPartialShipments) {
      if (quoteId === undefined || !isQuote) {
        cartItems.forEach((cartItem) => {
          if (cartItem.dateAvailable !== null) {
            if (latestNeedByDate === null) {
              latestNeedByDate = cartItem.dateAvailable;
            } else if (isoToDate(cartItem.dateAvailable) > isoToDate(latestNeedByDate)) {
              latestNeedByDate = cartItem.dateAvailable;
            }
          }
        });
      } else {
        quoteCartItemsShoppingCart.forEach((cartItem) => {
          if (cartItem.dateAvailable !== null) {
            if (latestNeedByDate === null) {
              latestNeedByDate = cartItem.dateAvailable;
            } else if (isoToDate(cartItem.dateAvailable) > isoToDate(latestNeedByDate)) {
              latestNeedByDate = cartItem.dateAvailable;
            }
          }
        });
      }
    }

    quoteId === undefined
      ? cartItems.forEach((cartItem) => {
          formattedCartItems.push({
            partId: cartItem.partId,
            partNumber: cartItem.partNumber,
            price: cartItem.price ? cartItem.price : 0,
            description: cartItem.name,
            quantity: cartItem.quantity,
            paintColorId: cartItem.paintColorId,
            impellerTrim: cartItem.impellerTrim,
            impellerAngle: cartItem.impellerAngle,
            shipBy: latestNeedByDate === null ? cartItem.dateAvailable : latestNeedByDate,
            needBy: latestNeedByDate === null ? cartItem.dateAvailable : latestNeedByDate,
          });
        })
      : quoteCartItemsShoppingCart.forEach((cartItem) => {
          formattedCartItems.push({
            partId: cartItem.partId,
            partNumber: cartItem.partNumber,
            price: cartItem.price ? cartItem.price : 0,
            description: cartItem.name,
            quantity: cartItem.quantity,
            paintColorId: cartItem.paintColorId,
            impellerTrim: cartItem.impellerTrim,
            impellerAngle: cartItem.impellerAngle,
            shipBy: latestNeedByDate === null ? cartItem.dateAvailable : latestNeedByDate,
            needBy: latestNeedByDate === null ? cartItem.dateAvailable : latestNeedByDate,
          });
        });
    return formattedCartItems;
  }

  // Send a request to Epicor to submit the sales order. If successful, navigate to the OrderCompletePage with the
  // returned order number as a path parameter.
  async function submitSalesOrder(): Promise<void> {
    const salesOrderRequestCode = crypto.randomUUID();
    const orderNeedBy = getOrderNeedBy();
    const requestBody = {
      salesOrderRequestCode: salesOrderRequestCode,
      shippingCountry: shippingCountry,
      shippingState: shippingState,
      shippingCity: shippingCity,
      shippingZipCode: shippingZipCode,
      shippingAddressLine1: shippingAddressLine1,
      shippingAddressLine2: shippingAddressLine2,
      shippingCompanyName: shippingCompanyName,
      shippingContactName: shippingContactName,
      shippingPhoneNumber: shippingPhoneNumber,
      comment: customerComment,
      allowPartialShipments: allowPartialShipments,
      shippingBillingType:
        shippingCarrier === null || shippingCarrier.billingType === null ? "" : shippingCarrier.billingType,
      shippingBillingAccountNumber: shippingBillingAccountNumber,
      specialInstructions: shippingSpecialInstructions,
      needBy: orderNeedBy,
      shipBy: orderNeedBy,
      shipVia: shipVia,
      poNumber: poNumber,
      cart: formatCartItemsForSubmission(),
      salesQuoteId: quoteId ? quoteId : null,
    };
    setShowProcessingModal(true);
    const [response, responseBody] = (await apiRequest(`${API}/sales-order`, "POST", requestBody)) as [
      Response,
      ResponseBodyPostSalesOrder
    ];
    // If we get an error message back before the request times out (happens when a PO number is duplicated).
    // Then show the specific error message as long as it is a user error.
    if (response.status >= 400 && response.status <= 499 && responseBody.error !== undefined) {
      setErrorMessage(responseBody.error);
      setShowProcessingModal(false);
      return;
    }
    // Begin polling for the results of the sales order being created in Epicor.
    let epicorSalesOrderId = null;
    let counter = 0;
    while (epicorSalesOrderId === null && counter <= SALES_ORDER_RETRY_LIMIT) {
      setShowProcessingModal(true);
      const [response, responseBody] = (await apiRequest(
        `${API}/sales-order/${salesOrderRequestCode}`,
        "GET",
        null
      )) as [Response, ResponseBodyOrderStatusGet];
      if (response.ok) {
        if (responseBody.requestStatus === "FAILED") {
          setShowProcessingModal(false);
          setErrorMessage("Unable to process order.");
          break;
        } else if (responseBody.epicorSalesOrderId !== null) {
          // Clear the cart and go to the order complete screen.
          epicorSalesOrderId = responseBody.epicorSalesOrderId;
          clearCart();
          setShowProcessingModal(false);
          navigate(`/order-complete/${responseBody.epicorSalesOrderId}`);
        } else {
          await delayMilliseconds(SALES_ORDER_DELAY_SECONDS * MS_PER_SECOND);
          counter = counter + 1;
        }
      } else {
        setErrorMessage(await getApiError(response, "Unable to process order."));
        setShowProcessingModal(false);
      }
    }
    setShowProcessingModal(false);
  }

  // Format the quote cart items into the format expected by the shopping cart.
  function formatQuoteCartItems(quoteCartItems: QuoteCartItemGet[]): QuoteCartItemShoppingCart[] {
    return quoteCartItems.map((item, i) => ({
      cartItemId: i + 1, // Use index + 1 as a unique ID for the cart item.
      name: item.description,
      partId: item.partId,
      partNumber: item.partNumber,
      isPaintable: item.isPaintable,
      paintColorId: item.paintColorId,
      quantity: item.quantity,
      quantityIsValid: true,
      units: item.units,
      price: item.price,
      weight: item.weight !== null ? item.weight : null,
      impellerTrim: item.impellerTrim !== null ? parseFloat(item.impellerTrim).toFixed(2).toString() : null,
      impellerTrimIsValid: true,
      impellerAngle: item.impellerAngle !== null ? parseInt(item.impellerAngle).toString() : null,
      impellerAngleIsValid: true,
      dateAvailable: null,
    }));
  }

  // Get the selected paint color id.
  function getPaintColorId(paintColors: PaintColor[], selectedPaintColorId: number | null): number | null {
    const paintColorIndex = paintColors.findIndex((paintColor) => paintColor.paintColorId === selectedPaintColorId);
    if (paintColorIndex > -1) {
      return paintColors[paintColorIndex].paintColorId;
    }
    return null;
  }

  // Function to prepare the cart items for the quote post request.
  function formatCartItemsForQuote(cartItems: CartItem[]): QuoteCartItemPost[] {
    const quoteCartItems: QuoteCartItemPost[] = [];
    cartItems.forEach((cartItem) => {
      quoteCartItems.push({
        partId: cartItem.partId,
        partNumber: cartItem.partNumber,
        quantity: cartItem.quantity,
        paintColorId: getPaintColorId(paintColors, cartItem.paintColorId),
        impellerTrim: cartItem.impellerTrim,
        impellerAngle: cartItem.impellerAngle,
        price: cartItem.price ? cartItem.price : 0,
        description: cartItem.name,
      });
    });
    return quoteCartItems;
  }

  // Function that creates a quote from the parts in the shopping cart.
  async function saveQuote(): Promise<void> {
    const cartItemsForQuote = formatCartItemsForQuote(cartItems);
    const requestBody = {
      quoteName: quoteName,
      poNumber: poNumber,
      cart: cartItemsForQuote,
      shippingCountry: shippingCountry,
      shippingState: shippingState,
      shippingCity: shippingCity,
      shippingDependentLocality: null,
      shippingZipCode: shippingZipCode,
      shippingSortingCode: null,
      shippingAddressLine1: shippingAddressLine1,
      shippingAddressLine2: shippingAddressLine2,
      shippingCompanyName: shippingCompanyName,
      shippingContactName: shippingContactName,
      shippingPhoneNumber: shippingPhoneNumber,
      comment: customerComment,
      allowPartialShipments: allowPartialShipments,
      shippingBillingType:
        shippingCarrier === null || shippingCarrier.billingType === null ? "" : shippingCarrier.billingType,
      shippingBillingAccountNumber: shippingBillingAccountNumber,
      specialInstructions: shippingSpecialInstructions,
      shipVia: shipVia,
    };
    setLoading(true);
    setErrorMessage("");
    const [response] = (await apiRequest(`${API}/sales-quote`, "POST", requestBody)) as [
      Response,
      ResponseBodyPostSalesQuote
    ];
    if (response.ok) {
      navigate("/sales-quotes");
    } else {
      setErrorMessage(await getApiError(response, "Unable to save quote."));
    }
    setLoading(false);
  }

  return failedToLoad ? (
    <Error500Page />
  ) : (
    <div className="container-fluid mt-5 mb-5 ">
      <div className="row justify-content-center">
        <div className="col-12 col-lg-6">
          <Spinner loading={loading} />
          <SaveCustomerAddressModal
            showModal={showSavedCustomerModal}
            title="Save Customer Address"
            content={"Enter a name for the saved address"}
            yesText="Save"
            noText="Cancel"
            danger={false}
            onClose={() => setShowSavedCustomerModal(false)}
            onYes={(name: string) => saveCustomerAddress(name)}
            onNo={() => setShowSavedCustomerModal(false)}
            savedCustomers={user.savedCustomers}
          />
          <ConfirmModal
            showModal={showDeleteCustomerModal}
            title="Delete Customer Address"
            content={
              selectedCustomerAddress !== null
                ? `Are you sure you want to delete '${selectedCustomerAddress.name}' customer information?`
                : `Are you sure you want to delete the customer information`
            }
            yesText="Delete"
            noText="Cancel"
            danger={true}
            onClose={() => setShowDeleteCustomerModal(false)}
            onYes={() => deleteSavedCustomerAddressInformation()}
            onNo={() => setShowDeleteCustomerModal(false)}
          />
          <div>
            <Accordion
              index={1}
              title={"Customer Information"}
              isActive={customerFormIsActive}
              isValid={customerFormIsValid}
              onChangeIsActive={(index, isActive) => handleAccordionIsActive(index, isActive, false)}
              onContinue={(index, isActive) => handleAccordionIsActive(index, isActive, true)}
              showContinueButton={true}
            >
              <CustomerForm
                name={user.name}
                companyName={user.companyName}
                email={user.email}
                phoneNumber={user.phoneNumber}
                savedCustomers={user.savedCustomers}
                poNumber={poNumber}
                isQuote={isQuote}
                quoteName={quoteName}
                shippingCountry={shippingCountry}
                shippingCompanyName={shippingCompanyName}
                shippingAddressLine1={shippingAddressLine1}
                shippingAddressLine2={shippingAddressLine2}
                shippingContactName={shippingContactName}
                shippingPhoneNumber={shippingPhoneNumber}
                shippingCity={shippingCity}
                shippingState={shippingState}
                shippingZipCode={shippingZipCode}
                validateShippingInformation={validateShippingInformation()}
                selectedCustomerAddress={selectedCustomerAddress}
                customerComment={customerComment}
                errorMessage={customerErrorMessage}
                warningMessage={customerWarningMessage}
                onChangePoNumber={(poNumber) => setPoNumber(poNumber)}
                onChangeQuoteName={(quoteName) => setQuoteName(quoteName)}
                onChangeShippingCountry={(shippingCountry) => setShippingCountry(shippingCountry)}
                onChangeShippingCompanyName={(shippingCompanyName) => setShippingCompanyName(shippingCompanyName)}
                onChangeShippingAddressLine1={(shippingAddressLine1) => setShippingAddressLine1(shippingAddressLine1)}
                onChangeShippingAddressLine2={(shippingAddressLine2) => setShippingAddressLine2(shippingAddressLine2)}
                onChangeShippingContactName={(shippingContactName) => setShippingContactName(shippingContactName)}
                onChangeShippingPhoneNumber={(shippingPhoneNumber) => setShippingPhoneNumber(shippingPhoneNumber)}
                onChangeShippingCity={(shippingCity) => setShippingCity(shippingCity)}
                onChangeShippingState={(shippingState) => setShippingState(shippingState)}
                onChangeShippingZipCode={(shippingZipCode) => setShippingZipCode(shippingZipCode)}
                onChangeCustomerComment={(customerComment) => setCustomerComment(customerComment)}
                getSavedCustomerAddressInformation={() => getSavedCustomerAddressInformation()}
                onChangeShowDeleteSavedCustomerModal={(showDeleteCustomerModal) =>
                  setShowDeleteCustomerModal(showDeleteCustomerModal)
                }
                onChangeSelectedCustomerAddress={(selectedCustomerAddress) =>
                  setSelectedCustomerAddress(selectedCustomerAddress)
                }
                onChangeSetShowSavedCustomerModal={(showSavedCustomerModal) =>
                  setShowSavedCustomerModal(showSavedCustomerModal)
                }
                onZipCodeInputBlur={() => validateZipCode()}
              />
            </Accordion>
          </div>
          <div>
            <Accordion
              index={2}
              title={"Shipping and Delivery"}
              isActive={shippingFormIsActive}
              isValid={shippingFormIsValid}
              onChangeIsActive={(index, isActive) => handleAccordionIsActive(index, isActive, false)}
              onContinue={(index, isActive) => handleAccordionIsActive(index, isActive, true)}
              showContinueButton={true}
            >
              <ShippingForm
                allowPartialShipments={allowPartialShipments}
                shippingCarrier={shippingCarrier}
                shippingBillingAccountNumber={shippingBillingAccountNumber}
                shippingSpecialInstructions={shippingSpecialInstructions}
                shipVia={shipVia}
                onChangeAllowPartialShipments={(allowPartialShipments) =>
                  setAllowPartialShipments(allowPartialShipments)
                }
                onChangeShippingCarrier={(shippingCarrier) => handleChangeShippingCarrier(shippingCarrier)}
                onChangeShipVia={(shipVia) => setShipVia(shipVia)}
                onChangeShippingBillingAccountNumber={(shippingBillingAccountNumber) =>
                  setShippingBillingAccountNumber(shippingBillingAccountNumber)
                }
                onChangeShippingSpecialInstructions={(shippingSpecialInstructions) =>
                  setShippingSpecialInstructions(shippingSpecialInstructions)
                }
              />
            </Accordion>
          </div>
          <div>
            <Accordion
              index={3}
              title={quoteId === undefined || !isQuote ? "Review Cart" : "Review Quote"}
              isActive={shoppingCartIsActive}
              isValid={shoppingCartIsValid}
              onChangeIsActive={(index, isActive) => handleAccordionIsActive(index, isActive, false)}
              onContinue={(index, isActive) => handleAccordionIsActive(index, isActive, true)}
              showContinueButton={true}
            >
              <ShoppingCart
                isQuote={isQuote}
                quoteId={quoteId}
                items={cartItems}
                quoteCartItems={quoteCartItemsShoppingCart}
                previousQuoteCartItems={previousQuoteCartItemsShoppingCart}
                paintColors={paintColors}
                companyName={user.companyName}
                companyDiscount={user.companyDiscount}
                distributorAccountCode={user.distributorAccountCode}
                accordionView={true}
                loading={loading}
                errorMessage={errorMessage}
                onUpdateMultiplePaintCartItems={(itemId, newPaintColorId, quantity) =>
                  updateMultiplePaintItems(itemId, newPaintColorId, quantity)
                }
                onUpdateCartItem={(
                  itemId,
                  quantity,
                  quantityIsValid,
                  paintColorId,
                  impellerTrim,
                  impellerTrimIsValid,
                  impellerAngle,
                  impellerAngleIsValid
                ) =>
                  updateCart(
                    itemId,
                    quantity,
                    quantityIsValid,
                    paintColorId,
                    impellerTrim,
                    impellerTrimIsValid,
                    impellerAngle,
                    impellerAngleIsValid
                  )
                }
                onUpdateCartItemDateAvailable={(itemId, dateAvailable) =>
                  updateCartItemDateAvailable(itemId, dateAvailable)
                }
                onUpdateQuoteCartItemDateAvailable={(itemId, dateAvailable) =>
                  updateCartItemDateAvailable(itemId, dateAvailable)
                }
                onDeleteCartItem={(itemId) => deleteCartItem(itemId)}
                onDeleteAllCartItems={() => {}}
              >
                {null}
              </ShoppingCart>
            </Accordion>
          </div>
          <div>
            <Accordion
              index={4}
              title={"Confirm Order"}
              isActive={confirmOrderFormIsActive}
              isValid={confirmOrderFormIsValid}
              onChangeIsActive={(index, isActive) => handleAccordionIsActive(index, isActive, false)}
              onContinue={(index, isActive) => handleAccordionIsActive(index, isActive, true)}
              showContinueButton={false}
            >
              <ConfirmOrderForm
                consentChecked={consentChecked}
                isQuote={isQuote}
                quoteId={quoteId}
                orderIsValid={orderIsValid}
                onChangeConsentChecked={(consentChecked) => {
                  handleConsentChecked(consentChecked);
                }}
                onSubmitOrder={() => submitSalesOrder()}
                onSaveQuote={() => saveQuote()}
                onChangeInvoiceOnlyChecked={(invoiceOnlyChecked) => {
                  setIsQuote(invoiceOnlyChecked);
                }}
                onErrorMessageChange={(message) => setErrorMessage(message)}
              />
            </Accordion>
          </div>
        </div>
      </div>
      <Toast
        title="Error"
        message={errorMessage}
        show={errorMessage.length > 0}
        onClose={() => setErrorMessage("")}
        type="error"
      />
      <ProcessingOrderModal showModal={showProcessingModal} />
    </div>
  );
}

interface Props {
  isNewQuote: boolean;
}

interface ResponseBodyMap {
  user: User;
  shoppingCart: ShoppingCartApiResponse;
  itemWeights: ItemWeight[];
}

interface ResponseBodySavedCustomerGet {
  shippingCountry: string;
  shippingAdministrativeArea: string;
  shippingLocality: string;
  shippingDependentLocality: string;
  shippingPostalCode: string;
  shippingSortingCode: string;
  shippingAddressLine1: string;
  shippingAddressLine2: string;
  shippingCompanyName: string;
  shippingContactName: string;
  shippingPhoneNumber: string;
  comment: string;
}

interface ResponseBodySavedCustomerPut {
  savedCustomerAddressId: number;
}

interface ResponseBodyPostSalesOrder {
  error: string;
}

interface ResponseBodyOrderStatusGet {
  requestStatus: string;
  epicorSalesOrderId: number | null;
}

interface ResponseBodyPostSalesQuote {
  message: string;
  salesQuoteId: number;
}

interface ResponseBodyGetSalesQuote {
  quoteName: string;
  poNumber: string;
  cart: QuoteCartItemGet[];
  companyDiscount: number | null;
  distributorName: string | null;
  grandTotal: number;
  shippingCountry: string;
  shippingState: string;
  shippingCity: string;
  shippingZipCode: number;
  shippingAddressLine1: string;
  shippingAddressLine2: string | null;
  shippingCompanyName: string;
  shippingContactName: string;
  shippingPhoneNumber: string;
  comment: string | null;
  allowPartialShipments: boolean;
  shippingBillingType: string | null;
  shippingBillingAccountNumber: string | null;
  specialInstructions: string | null;
  shipVia: string | null;
  subTotal: number;
}

interface User {
  name: string;
  companyName: string;
  companyCode: string;
  email: string;
  phoneNumber: string;
  companyDiscount: number | null;
  savedCustomers: SavedCustomer[];
  distributorAccountCode: string | null;
}

interface SavedCustomer {
  savedCustomerAddressId: number;
  name: string;
}

interface ShoppingCart {
  items: CartItem[];
  paintColors: PaintColor[];
}

interface PaintColor {
  paintColorId: number;
  hexCode: string;
  name: string;
}

interface ShoppingCartApiResponse {
  items: CartItemApiResponse[];
  paintColors: PaintColor[];
  companyName: string | null;
  companyDiscount: number | null;
}

interface CartItemApiResponse {
  cartItemId: number;
  name: string;
  partId: number;
  partNumber: string;
  isPaintable: boolean;
  paintColorId: number | null;
  quantity: string;
  units: string;
  price: number;
  weight: number | null;
  impellerTrim: string | null;
  impellerAngle: string | null;
}

interface CartItem {
  cartItemId: number;
  name: string;
  partId: number;
  partNumber: string;
  isPaintable: boolean;
  paintColorId: number | null;
  quantity: string;
  quantityIsValid: boolean;
  units: string;
  price: number | null;
  weight: number | null;
  impellerTrim: string | null;
  impellerTrimIsValid: boolean;
  impellerAngle: string | null;
  impellerAngleIsValid: boolean;
  dateAvailable: string | null;
}

interface CartItemPost {
  partId: number;
  price: number;
  description: string;
  partNumber: string;
  paintColorId: number | null;
  quantity: string;
  impellerTrim: string | null;
  impellerAngle: string | null;
  needBy: string | null;
  shipBy: string | null;
}

interface QuoteCartItemPost {
  partId: number;
  price: number;
  description: string;
  partNumber: string;
  paintColorId: number | null;
  quantity: string;
  impellerTrim: string | null;
  impellerAngle: string | null;
  needBy?: string | null;
  shipBy?: string | null;
}

interface QuoteCartItemGet {
  partId: number;
  partNumber: string;
  quantity: string;
  paintColorId: number | null;
  isPaintable: boolean;
  impellerTrim: string | null;
  impellerAngle: string | null;
  price: number;
  description: string;
  units: string;
  weight: number | null;
}

interface QuoteCartItemShoppingCart {
  cartItemId: number;
  name: string; //Description of the part.
  partId: number;
  partNumber: string;
  isPaintable: boolean;
  paintColorId: number | null;
  quantity: string;
  quantityIsValid: boolean;
  units: string;
  price: number | null;
  weight: number | null;
  impellerTrim: string | null;
  impellerTrimIsValid: boolean;
  impellerAngle: string | null;
  impellerAngleIsValid: boolean;
  dateAvailable: string | null;
}

interface ItemWeight {
  partNumber: string;
  weight: number;
}

interface Carrier {
  name: string;
  types: ShipVia[];
  billingType: string | null;
}

interface ShipVia {
  name: string;
  code: string;
}
